import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Mentions } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ExternalLink, Trash2, Globe, Loader2, Search, RefreshCw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

export default function MentionsView() {
  const { activeShowId, activeShow } = useApp();
  const [items, setItems] = useState(null);
  const [searching, setSearching] = useState(false);

  const load = () => Mentions.list(activeShowId).then(setItems);
  useEffect(() => { setItems(null); load(); }, [activeShowId]); // eslint-disable-line

  const remove = async (id) => { await Mentions.remove(id); load(); };

  const runSearch = async () => {
    setSearching(true);
    try {
      const r = await Mentions.searchNow();
      if (r.ok) {
        const s = r.summary || {};
        toast({
          title: 'Search complete',
          description: `${s.queries || 0} guests queried · ${s.new || 0} new mention${(s.new || 0) === 1 ? '' : 's'}`,
        });
      } else if (r.reason === 'no_serper_key') {
        toast({ title: 'Add Serper key', description: 'Settings → Web Monitoring → Serper API key' });
      } else {
        toast({ title: 'Search skipped', description: r.reason || 'Unknown' });
      }
      await load();
    } catch (e) {
      toast({ title: 'Search failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setSearching(false);
    }
  };

  if (!items) return <div className="h-full grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="p-5 overflow-auto h-full">
      <div className="flex items-end justify-between mb-4 gap-3">
        <div>
          <h1 className="text-[24px] font-serif text-stone-900">Mentions</h1>
          <p className="text-[12.5px] text-stone-500">Web search results for {activeShow.name} guests · runs automatically every Monday 8am, or click below to run now.</p>
        </div>
        <Button data-testid="mentions-search-now-btn" onClick={runSearch} disabled={searching} className="text-white" style={{ background: activeShow.color }}>
          {searching ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
          {searching ? 'Searching…' : 'Search mentions now'}
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="p-10 text-center text-stone-500">
            <Globe className="w-8 h-8 mx-auto mb-2 text-stone-300" />
            <div className="text-[14px] mb-1">No mentions yet</div>
            <div className="text-[12px] text-stone-400">Add a Serper.dev API key in Settings, then click {'"\u201CSearch mentions now\u201D"'} above. Only guests with status <i>recorded</i>, <i>edited</i>, or <i>published</i> are searched.</div>
            <Button variant="outline" size="sm" className="mt-4" onClick={runSearch} disabled={searching}>
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${searching ? 'animate-spin' : ''}`} /> Try a search now
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map(m => (
            <Card key={m.id} className="border-stone-200 hover:shadow-sm transition-shadow">
              <CardContent className="p-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-700 font-medium">{m.guestName}</span>
                    {m.source && <span className="text-[11px] text-stone-400">{m.source}</span>}
                  </div>
                  <a href={m.url} target="_blank" rel="noreferrer" className="text-[14px] font-medium text-stone-900 hover:underline truncate block">{m.title || m.url}</a>
                  <div className="text-[12.5px] text-stone-600 line-clamp-2 mt-0.5">{m.snippet}</div>
                  <div className="text-[11px] text-stone-400 mt-1">{new Date(m.foundAt).toLocaleDateString()} • query: <code className="text-stone-500">{m.query}</code></div>
                </div>
                <div className="flex flex-col gap-1">
                  <Button size="sm" variant="outline" onClick={() => window.open(m.url, '_blank')}><ExternalLink className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(m.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
